import { memo, useMemo } from "react"

import {
  ActionTypes,
  type ActionType,
  type OverTimeStatistic
} from "@/api/useWorkflow.types"
import { Column } from "@/atoms"
import { getDateInFormat, MONTH_DAY_SHORT_FORMAT } from "@/helpers/date.helpers"
import { useNumberConvertor } from "@/hooks/useNumberConvertor"
import { useTranslation } from "@/hooks/useTranslation"
import { Widget } from "@/molecules/Widget"
import { ActionsOverTimeTooltip } from "./ActionsOverTimeTooltip"

import type { ActionOverTime } from "./ActionsOverTimeTooltip"
import type { ActionsOverTimeWidgetComponentProps } from "./ActionsOverTimeWidget.types"

const actionTypes = Object.values(ActionTypes) as ActionType[]

// The grouped chart's dodge spaces only the series a week has rows for, so zero-count actions are left out
// rather than reserving an empty slot; a week with no actions keeps one zero row so it stays on the axis.
export const toChartData = (
  weeks: OverTimeStatistic[],
  formatValue: (value: number) => string
) =>
  weeks.flatMap((week) => {
    const year = `${getDateInFormat(week.weekStart, MONTH_DAY_SHORT_FORMAT)} - ${getDateInFormat(week.weekEnd, MONTH_DAY_SHORT_FORMAT)}`
    const present = actionTypes.filter((type) => (week.actions[type] || 0) > 0)
    return (present.length ? present : actionTypes.slice(0, 1)).map((type) => {
      const value = week.actions[type] || 0
      return { type, value, valueTooltip: formatValue(value), year }
    })
  })

const ACTION_COLOR_VARIABLES: Record<ActionType, string> = {
  webhook: "--webhook-icon-color",
  label: "--label-icon-color",
  dismiss: "--dismiss-icon-color",
  jira_issue: "--jira-icon-color",
  reopen: "--reopen-icon-color",
  slack_message: "--slack-icon-color",
  azure_boards_issue: "--azure-boards-icon-color",
  asana_task: "--asana-icon-color",
  github_issue: "--github-icon-color"
}

export const getActionColors = (
  style: Pick<CSSStyleDeclaration, "getPropertyValue">
) =>
  Object.fromEntries(
    actionTypes.map((type) => [
      type,
      style.getPropertyValue(ACTION_COLOR_VARIABLES[type])
    ])
  )

export const ActionsOverTimeWidgetComponent: React.FC<ActionsOverTimeWidgetComponentProps> =
  memo(
    function ActionsOverTimeWidgetComponent({ data, isLoading }) {
      const { t } = useTranslation("translation", {
        keyPrefix: "settings.automatedActions.widgets.overtime"
      })
      const { intlNumber, abbreviateNumber } = useNumberConvertor()

      const config = useMemo(() => {
        const ACTION_COLORS = getActionColors(
          getComputedStyle(document.documentElement)
        )

        const res = data || []
        const chartData = toChartData(res, intlNumber)

        const max = Math.max(
          ...res.flatMap((items) =>
            actionTypes.map((type) => items.actions[type] || 0)
          )
        )

        return {
          data: chartData,
          height: 152,
          maxColumnWidth: 12,
          legend: false,
          isGroup: true,
          dodgePadding: 2,
          xField: "year",
          yField: "value",
          xAxis: {
            label: {
              style: {
                fill: "rgba(0, 0, 0, 0.45)",
                fontSize: 12
              }
            },
            line: null,
            tickLine: null
          },
          yAxis: {
            type: "log",
            max: max === 0 ? undefined : max + Math.round(max * 0.1),
            title: null,
            label: {
              style: {
                fill: "rgba(0, 0, 0, 0.45)",
                fontSize: 12
              },
              formatter: (value: string) => abbreviateNumber(Number(value))
            },
            grid: {
              line: {
                style: {
                  stroke: "#f0f0f0"
                }
              }
            }
          },
          offsetY: 100,
          seriesField: "type",
          color: ({ type }: { type: ActionType }) => ACTION_COLORS[type],
          interactions: [
            {
              type: "active-region",
              enable: false
            }
          ],
          responsive: true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [data]) as any

      return (
        <Widget
          loading={isLoading}
          title={t("title")}
          popover={{ content: t("tooltip") }}
        >
          <Column
            tooltip={{
              customContent: (_, chartData) => {
                return (
                  <ActionsOverTimeTooltip
                    actions={
                      chartData
                        .filter((item) => item.data.value > 0)
                        .map((item) => item.data) as ActionOverTime[]
                    }
                  />
                )
              }
            }}
            {...config}
          />
        </Widget>
      )
    },
    (prev, curr) => prev.data === curr.data
  )
